import { MainLayout } from "@/App/layouts/MainLayout";
import { SingleComponentLayout } from "@/App/layouts/SingleComponentLayout";
import { FC, useContext, useMemo } from "react";
import { Route, Routes } from "react-router";
import { AuthnzWrapper, ClientAwareNavigate } from "../components";
import { AuthenticationContext, ExperienceContext } from "../context";
import { RouteProps, appRoutes, authnzRoutes, mfaRoutes } from "./routes";

export const MainRoutes: FC = () => {
  const { authState } = useContext(AuthenticationContext);
  const {
    hideMemberClaimsPage,
    hideFindBestPricePage,
    hideInboxPage,
    hideFormDocumentsPage,
    hideNearbyPharmaciesPage,
    hideDashboard,
    hidePreferences,
    hidePriorAuthorization,
    hideEligibility,
    hideMedicarePrescriptionPaymentPlan,
    hideDirectMemberReimbursement,
    overrideLandingPage,
  } = useContext(ExperienceContext);

  const authRoutes = useMemo(() => {
    return authnzRoutes.map(({ path, Component, pageTitle, pageHeader }) => {
      return (
        <Route
          key={path}
          path={path}
          element={
            <AuthnzWrapper pageTitle={pageTitle} pageHeader={pageHeader}>
              {/* @ts-ignore */}
              <Component />
            </AuthnzWrapper>
          }
        />
      );
    });
  }, []);

  const mfaLoginRoutes = useMemo(() => {
    return mfaRoutes.map(({ path, Component, pageTitle, pageHeader }) => {
      return (
        <Route
          key={path}
          path={path}
          element={
            <AuthnzWrapper bypassLoginRequired pageTitle={pageTitle} pageHeader={pageHeader}>
              {/* @ts-ignore */}
              <Component />
            </AuthnzWrapper>
          }
        />
      );
    });
  }, [mfaRoutes]);

  const applicationRoutes = appRoutes.map(({ path, pageTitle, label, Component, showIconLogo }: RouteProps) => {
    const MainAppComponents = () => {
      if (!["signedIn", "loading", "verifyContact", "ssoCallback", "emulationCallback"].includes(authState)) {
        return (
          <ClientAwareNavigate
            to={{
              pathname: "/login",
            }}
          />
        );
      }

      if (hideInboxPage && path.includes("/inbox")) {
        return;
      }

      if (hideMemberClaimsPage && path.includes("/claims-history")) {
        return;
      }

      if (hideFindBestPricePage && path.includes("/find-best-price")) {
        return;
      }

      if (hideFormDocumentsPage && path.includes("/forms-and-documents")) {
        return;
      }

      if (hideNearbyPharmaciesPage && path.includes("/nearby-pharmacy")) {
        return;
      }

      if (hideDirectMemberReimbursement && path.includes("/direct-member-reimbursement")) {
        return;
      }

      if (hideDashboard && path === "/") {
        if (overrideLandingPage) {
          const parts = overrideLandingPage.split("?");
          return (
            <ClientAwareNavigate
              to={{
                pathname: parts[0],
                search: parts.length > 1 ? parts[1] : undefined,
              }}
            />
          );
        } else {
          return;
        }
      }

      if (hidePreferences && path.includes("/notifications")) {
        return;
      }

      if (hidePriorAuthorization && path.includes("/prior-authorization")) {
        return;
      }

      if (hideEligibility && path.includes("/eligibility")) {
        return;
      }

      if (hideMedicarePrescriptionPaymentPlan && path.includes("/medicare-prescription-payment-plan")) {
        return;
      }

      if (["/id_card", "/print-pharmacy-claims-history", "/print-medical-claims-history", "/print-prior-authorization-page", "/print-medical-claim-details"].includes(path)) {
        return (
          <SingleComponentLayout pageTitle={pageTitle} label={label} showIconLogo={showIconLogo}>
            <Component pageTitle={pageTitle} />
          </SingleComponentLayout>
        );
      } else {
        return (
          <MainLayout pageTitle={pageTitle} label={label} showIconLogo={showIconLogo}>
            <Component pageTitle={pageTitle} />
          </MainLayout>
        );
      }
    };

    return (
      <Route
        key={path}
        path={path}
        // @ts-ignore
        pageTitle={pageTitle}
        element={<MainAppComponents />}
      />
    );
  });

  return (
    <Routes>
      {authRoutes}
      {mfaLoginRoutes}
      {applicationRoutes}
    </Routes>
  );
};
